import { css, cx } from '@linaria/core'
import React, { type FC, type ReactNode, useId } from 'react'
import { fragments, style } from '#styles/index'

const inputWithErrorClass = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: ${style.themeColors.textImportant};
  ${fragments.textStyle.body.s.regular};
  
  > .label {
    color: ${style.themeColors.textDefault};
    ${fragments.textStyle.body.s.bold}
    > .required {
      color: ${style.themeColors.textImportant};
    }
  }

  > .error {
    min-height: 18px;
  }
`

export type InputLayoutProps = {
  error?: ReactNode | boolean
  label?: ReactNode
  required?: boolean
  slim?: boolean
  prepareLabelMargins?: boolean
  prepareErrorMargins?: boolean
  id?: string
  // biome-ignore lint/suspicious/noExplicitAny: no better option
  children: React.ReactElement<{ id?: string; className?: string }, string | React.JSXElementConstructor<any>>
  className?: string
}

export type InputLayoutControlProps = Omit<InputLayoutProps, 'children'>

export type StripInputProps<T> = Omit<T, keyof InputLayoutControlProps | 'type'>
export const InputLayout: FC<InputLayoutProps> = ({
  id: externalId,
  error,
  label,
  required,
  slim,
  prepareLabelMargins,
  prepareErrorMargins,
  children,
  className,
}) => {
  const reactId = useId()
  const id = externalId ?? reactId

  // const withoutError = prepareErrorMargins !== false && !error
  // const withoutLabel = prepareLabelMargins !== false && !error
  const baseInputClasses = [
    prepareErrorMargins !== false && !error && 'without-error',
    prepareLabelMargins !== false && !label && 'without-label',
    slim && 'slim',
  ]

  if (error || label || prepareErrorMargins || prepareLabelMargins) {
    return (
      <div className={cx(inputWithErrorClass, className)}>
        {label && (
          <label className="label" htmlFor={id}>
            {label} {required && <span className="required">*</span>}
          </label>
        )}
        {React.Children.map(children, (child) =>
          React.cloneElement(child, { id: child.props.id ?? id, className: cx(child.props.className, ...baseInputClasses) }),
        )}

        {error && <span className="error">{error}</span>}
      </div>
    )
  }

  return React.Children.map(children, (child) =>
    React.cloneElement(child, { id: child.props.id ?? id, className: cx(child.props.className, ...baseInputClasses) }),
  )
}
